import type { AppointmentServiceConfigRead } from "@/types/appointments/api-types/appointment-types";

const toCents = (value: number | string): number => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.round(numeric * 100);
};

/** Display-only sale total matching SalesService._compute_totals (2 dp). */
export const computeSaleFormTotal = (
  servicePrices: Array<number | string>,
  productPrices: Array<number | string>,
  discountPercent: number | string
): number => {
  const subtotalCents =
    servicePrices.reduce<number>((sum, price) => sum + toCents(price), 0) +
    productPrices.reduce<number>((sum, price) => sum + toCents(price), 0);
  const factor = 1 - Number(discountPercent) / 100;
  return Math.round(subtotalCents * factor) / 100;
};

const sumPaymentAmounts = (amounts: Array<number | string>): number =>
  amounts.reduce<number>((sum, amount) => sum + toCents(amount), 0) / 100;

export const paymentsMatchTotal = (
  amounts: Array<number | string>,
  total: number
): boolean => sumPaymentAmounts(amounts) === toCents(total) / 100;

export const saleClientLabel = (
  clientName: string | null | undefined
): string => {
  const trimmed = clientName?.trim();
  return trimmed ? trimmed : "this client";
};

const addMinutesToHhmm = (hhmm: string, durationMinutes: number): string => {
  const [hourRaw, minuteRaw] = hhmm.split(":");
  const startMinutes = Number(hourRaw) * 60 + Number(minuteRaw) + durationMinutes;
  const wrapped = ((startMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
  const hour = Math.floor(wrapped / 60);
  const minute = wrapped % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

/** The sale's first start and last end (`HH:MM`), or null without services. */
export const saleTimeSpan = (
  services: Pick<AppointmentServiceConfigRead, "start_time" | "duration_minutes">[]
): { start: string; end: string } | null => {
  if (services.length === 0) return null;
  let earliest = services[0].start_time;
  let latestEnd = addMinutesToHhmm(
    services[0].start_time,
    services[0].duration_minutes
  );
  for (const line of services.slice(1)) {
    if (line.start_time < earliest) earliest = line.start_time;
    const end = addMinutesToHhmm(line.start_time, line.duration_minutes);
    if (end > latestEnd) latestEnd = end;
  }
  return { start: earliest, end: latestEnd };
};
